import React, { createContext, useState, useContext, useEffect } from 'react';
import api, { AUTH_MODE } from '../api/api';

const AuthContext = createContext();

// ISO 27001 A.9.4.2 — Absolute session limit (8 hours)
const ABSOLUTE_SESSION_LIMIT_MS = 8 * 60 * 60 * 1000;

export const useAuth = () => {
  return useContext(AuthContext);
};

const isSessionExpired = () => {
  const loginTime = localStorage.getItem('sessionStartTime');
  if (!loginTime) return true;
  return (Date.now() - parseInt(loginTime, 10)) >= ABSOLUTE_SESSION_LIMIT_MS;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check absolute session limit first
        if (isSessionExpired()) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('sessionStartTime');
          localStorage.removeItem('payoutToken');
          localStorage.removeItem('lastActivityTime');
          sessionStorage.clear();
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        // Check if browser was closed and reopened (sessionStorage flag cleared on close)
        if (!sessionStorage.getItem('browserSessionActive')) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('sessionStartTime');
          localStorage.removeItem('payoutToken');
          localStorage.removeItem('lastActivityTime');
          sessionStorage.clear();
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        if (AUTH_MODE === 'header') {
          const token = localStorage.getItem('token');
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              const expiresAt = payload.exp * 1000;
              if (Date.now() >= expiresAt) {
                const refreshToken = localStorage.getItem('refreshToken');
                if (refreshToken) {
                  const response = await api.post(`/api/auth/refresh?refreshToken=${encodeURIComponent(refreshToken)}`);
                  if (response.data.success) {
                    const { token: newToken, refreshToken: newRefreshToken } = response.data.data;
                    if (newToken) localStorage.setItem('token', newToken);
                    if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
                    const userData = JSON.parse(localStorage.getItem('userData'));
                    setCurrentUser(userData);
                  } else {
                    throw new Error('Refresh failed');
                  }
                } else {
                  throw new Error('No refresh token');
                }
              } else {
                const userData = JSON.parse(localStorage.getItem('userData'));
                setCurrentUser(userData);
              }
            } catch (e) {
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('userData');
              localStorage.removeItem('sessionStartTime');
              setCurrentUser(null);
            }
          }
        } else {
          const response = await api.get('/api/auth/me');
          if (response.data.success && response.data.data) {
            setCurrentUser(response.data.data);
          }
        }
      } catch (error) {
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (username, password, expectedRole = null, adminCode = null) => {
    try {
      const loginData = { username, password };
      if (adminCode) loginData.adminCode = adminCode;

      const response = await api.post('/api/auth/login', loginData);

      if (response.data.success) {
        const userData = response.data.data;

        if (expectedRole && userData.role !== expectedRole) {
          return { success: false, error: `Invalid credentials for ${expectedRole.toLowerCase()} access` };
        }

        if (AUTH_MODE === 'header') {
          if (userData.token) {
            localStorage.setItem('token', userData.token);
            localStorage.setItem('userData', JSON.stringify(userData));
          }
          if (userData.refreshToken) {
            localStorage.setItem('refreshToken', userData.refreshToken);
          }
          // Record absolute session start time
          localStorage.setItem('sessionStartTime', Date.now().toString());
          localStorage.setItem('lastActivityTime', Date.now().toString());
          sessionStorage.setItem('browserSessionActive', 'true');
        }

        setCurrentUser(userData);
        return { success: true };
      }
      return { success: false, error: 'Login failed' };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.response?.data?.message || 'Login failed' };
    }
  };

  const signup = async (signupData) => {
    try {
      const requestData = { role: 'MERCHANT', ...signupData };
      const response = await api.post('/api/auth/signup', requestData);

      if (response.data.success) {
        const userData = response.data.data;
        if (AUTH_MODE === 'header') {
          if (userData.token) {
            localStorage.setItem('token', userData.token);
            localStorage.setItem('userData', JSON.stringify(userData));
          }
          if (userData.refreshToken) {
            localStorage.setItem('refreshToken', userData.refreshToken);
          }
          localStorage.setItem('sessionStartTime', Date.now().toString());
          sessionStorage.setItem('browserSessionActive', 'true');
        }
        setCurrentUser(userData);
        return { success: true };
      }
      return { success: false, error: 'Signup failed' };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.response?.data?.message || 'Signup failed' };
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      const params = refreshToken ? `?refreshToken=${encodeURIComponent(refreshToken)}` : '';
      await api.post(`/api/auth/logout${params}`);
    } catch (error) {}
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionStartTime');
    localStorage.removeItem('payoutToken');
    sessionStorage.clear();
    setCurrentUser(null);
  };

  const value = { currentUser, login, signup, logout };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
